import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search, CalendarClock, Check, X, Video, Phone } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rescheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  declined: 'bg-destructive/10 text-destructive border-destructive/20',
  cancelled: 'bg-secondary text-muted-foreground border-border',
  completed: 'bg-secondary text-muted-foreground border-border'
};

const STATUS_LABEL = {
  pending: 'Awaiting confirmation',
  approved: 'Confirmed by Bandhani Heritage',
  confirmed: 'Confirmed',
  rescheduled: 'New time suggested',
  declined: 'Not available',
  cancelled: 'Cancelled',
  completed: 'Completed'
};

const pretty = (date, slot) =>
  `${new Date(`${date}T${slot}`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long' })} · ${slot} IST`;

const MyBookings = () => {
  const [params] = useSearchParams();
  const [phone, setPhone] = useState(params.get('phone') || '');
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (value) => {
    const num = (value ?? phone).trim();
    if (!num) { toast.error('Enter the mobile number you booked with'); return; }
    setLoading(true);
    try {
      const res = await api.get('/appointments/lookup', { params: { phone: num } });
      setBookings(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get('phone')) load(params.get('phone'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptProposal = async (id) => {
    try {
      await api.put(`/appointments/${id}/accept-proposal`, null, { params: { phone } });
      toast.success('New time accepted — see you then');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept');
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await api.put(`/appointments/${id}/cancel`, null, { params: { phone } });
      toast.success('Appointment cancelled');
      load();
    } catch {
      toast.error('Could not cancel');
    }
  };

  return (
    <div className="py-24 md:py-32 max-w-3xl mx-auto px-4 md:px-8" data-testid="my-bookings-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Appointments</p>
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        My Bookings
      </h1>
      <p className="text-base text-muted-foreground mt-3">Enter the mobile number you booked with to see the status.</p>

      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mobile number</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-full mt-2" data-testid="lookup-phone-input" />
        </div>
        <div className="flex items-end">
          <Button className="rounded-full" onClick={() => load()} disabled={loading} data-testid="lookup-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Check status
          </Button>
        </div>
      </div>

      {bookings !== null && (
        <div className="mt-10 space-y-4" data-testid="bookings-list">
          {bookings.length === 0 && (
            <div className="border border-dashed border-border rounded-xl p-10 text-center">
              <CalendarClock className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No bookings found for this number.</p>
              <Link to="/book-call"><Button variant="outline" className="rounded-full mt-4">Book a call</Button></Link>
            </div>
          )}
          {bookings.map((b) => (
            <div key={b.id} className="border border-border rounded-xl p-5" data-testid={`booking-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="flex items-center gap-2 font-medium">
                    {b.call_type === 'video' ? <Video className="h-4 w-4 text-primary" /> : <Phone className="h-4 w-4 text-primary" />}
                    {b.call_type === 'video' ? 'Video call' : 'Phone call'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{pretty(b.date, b.slot)}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${STATUS_STYLE[b.status] || 'bg-secondary border-border'}`} data-testid={`booking-status-${b.id}`}>
                  {STATUS_LABEL[b.status] || b.status}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {b.interest_item ? `Interested in: ${b.interest_item}` : `Budget: ${b.price_range}`}
              </p>
              {b.admin_message && (
                <p className="text-sm mt-3 bg-secondary/60 rounded-lg p-3">“{b.admin_message}”</p>
              )}
              {b.status === 'rescheduled' && b.proposed_slot && (
                <div className="mt-4 border border-blue-200 bg-blue-50/60 rounded-lg p-3">
                  <p className="text-sm text-blue-900">New time suggested: <span className="font-medium">{pretty(b.proposed_date, b.proposed_slot)}</span></p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="rounded-full" onClick={() => acceptProposal(b.id)} data-testid={`accept-proposal-${b.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Accept this time
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => cancel(b.id)} data-testid={`cancel-booking-${b.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              )}
              {['pending', 'approved', 'confirmed'].includes(b.status) && (
                <Button size="sm" variant="ghost" className="rounded-full mt-3 text-muted-foreground" onClick={() => cancel(b.id)} data-testid={`cancel-booking-${b.id}`}>
                  Cancel appointment
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyBookings;
